import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { Download, ExternalLink, FolderOpen, FolderTree, Loader2, Pencil, Search, Trash2, UploadCloud, X } from 'lucide-react';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { ConfirmDialog, Modal } from '@components/Dialog';
import { Pagination } from '@components/Pagination';
import { ActionButton } from '@components/Table';
import { FileTypeIcon } from '@components/FileTypeIcon';
import { ExpiryBadge, ExpiryLabel } from '@components/ExpiryBadge';
import { usePermissions } from '@hooks/usePermissions';
import documentService, { flattenFolders } from '@services/documentService';
import { getConfig } from '../../config';
import type { Pagination as PaginationInfo } from '@/types/activity';
import type { Document, DocumentFolder } from '@/types/document';
import { formatBytes, formatDateBR } from '@/utils/formatters';
import { FolderPanel } from './FolderPanel';
import { DocumentUploadModal } from './DocumentUploadModal';
import { DocumentEditModal } from './DocumentEditModal';
import { downloadDocument, openDocumentInNewTab } from './documentActions';

const countAll = (tree: DocumentFolder[]): number => tree.reduce((n, f) => n + (f.document_count ?? 0) + countAll(f.children ?? []), 0);

/**
 * Documentos: pastas (árvore à esquerda; no celular, seletor + botão "Pastas") e a lista de
 * documentos da pasta selecionada, com busca, filtro "vencendo" e paginação.
 * `?folder=<id>`, `?expiring=1` e `?open=<id>` (sino / painel Hoje) são lidos da URL.
 */
export const DocumentsView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('documentos.write').hasPermission;
  const [searchParams, setSearchParams] = useSearchParams();
  const pageSize = getConfig().ui.itemsPerPage || 20;

  const [folders, setFolders] = useState<DocumentFolder[]>([]);
  const [folderId, setFolderId] = useState<number | null>(() => (Number(searchParams.get('folder')) > 0 ? Number(searchParams.get('folder')) : null));
  const [expiring, setExpiring] = useState(searchParams.get('expiring') === '1');
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<Document[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [foldersOpen, setFoldersOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState<Document | null>(null);
  const [deleting, setDeleting] = useState<Document | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState<number | null>(null);

  const loadFolders = useCallback(async () => {
    try {
      setFolders(await documentService.getFolders());
    } catch (err) {
      setError(err);
    }
  }, []);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setQ((old) => (old === searchInput.trim() ? old : searchInput.trim()));
      setPage(1);
    }, 350);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await documentService.list({ folder_id: folderId ?? undefined, q: q || undefined, expiring, page, page_size: pageSize });
      setItems(res.data);
      setPagination(res.pagination);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [folderId, q, expiring, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  // ?open=<id> → abre a edição direto (link do sino / painel Hoje) e limpa o parâmetro.
  useEffect(() => {
    const openId = Number(searchParams.get('open'));
    if (openId > 0) {
      documentService.getById(openId).then(setEditing).catch(setError);
      const next = new URLSearchParams(searchParams);
      next.delete('open');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const selectFolder = (id: number | null) => {
    setFolderId(id);
    setPage(1);
    setFoldersOpen(false);
    const next = new URLSearchParams(searchParams);
    if (id) next.set('folder', String(id));
    else next.delete('folder');
    setSearchParams(next, { replace: true });
  };

  const flat = useMemo(() => flattenFolders(folders), [folders]);
  const selectedFolder = flat.find((x) => x.folder.id === folderId)?.folder ?? null;
  const total = useMemo(() => countAll(folders), [folders]);

  const refreshAll = () => {
    loadFolders();
    load();
  };

  const run = async (doc: Document, fn: (id: number) => Promise<void>) => {
    setActionBusy(doc.id);
    try {
      await fn(doc.id);
    } catch (err) {
      setError(err);
    } finally {
      setActionBusy(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await documentService.remove(deleting.id);
      setDeleting(null);
      refreshAll();
    } catch (err) {
      setDeleting(null);
      setError(err);
    } finally {
      setDeleteBusy(false);
    }
  };

  const actions = (d: Document) => (
    <div className="flex items-center gap-0.5 justify-end">
      <ActionButton icon={<ExternalLink size={16} />} variant="view" tooltip="Visualizar" disabled={actionBusy === d.id} onClick={() => run(d, openDocumentInNewTab)} />
      <ActionButton icon={<Download size={16} />} variant="default" tooltip="Baixar" disabled={actionBusy === d.id} onClick={() => run(d, downloadDocument)} />
      {canWrite && (
        <>
          <ActionButton icon={<Pencil size={16} />} variant="edit" tooltip="Editar" onClick={() => setEditing(d)} />
          <ActionButton icon={<Trash2 size={16} />} variant="delete" tooltip="Excluir" onClick={() => setDeleting(d)} />
        </>
      )}
    </div>
  );

  const hasFilters = !!q || expiring;

  const folderPanel = (
    <FolderPanel folders={folders} selectedId={folderId} onSelect={selectFolder} onChanged={refreshAll} onError={setError} canWrite={canWrite} totalCount={total} />
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3 flex items-center gap-2">
          <FolderOpen size={20} className="text-slate-500" /> Documentos
        </h1>
        {canWrite && (
          <Button variant="primary" size="sm" icon={<UploadCloud />} onClick={() => setUploading(true)}>
            Enviar documentos
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[18rem_1fr] gap-4 items-start">
        {/* Pastas: painel fixo no desktop; seletor + botão no celular */}
        <aside className="card p-3 hidden lg:block lg:sticky lg:top-0">{folderPanel}</aside>
        <div className="lg:hidden grid grid-cols-[1fr_auto] gap-2">
          <select className="select-field" value={folderId ?? ''} onChange={(e) => selectFolder(e.target.value ? Number(e.target.value) : null)}>
            <option value="">Todas as pastas ({total})</option>
            {flat.map(({ folder, depth }) => (
              <option key={folder.id} value={folder.id}>
                {`${'   '.repeat(depth)}${depth > 0 ? '↳ ' : ''}${folder.name} (${folder.document_count})`}
              </option>
            ))}
          </select>
          <Button variant="outline" size="sm" icon={<FolderTree />} onClick={() => setFoldersOpen(true)} title="Gerenciar pastas">
            Pastas
          </Button>
        </div>

        <section className="min-w-0 space-y-3">
          <div className="card p-3 sm:p-4 grid grid-cols-1 sm:flex sm:items-end gap-3">
            <div className="relative flex-1">
              <label className="text-label block mb-1">Buscar</label>
              <Search size={16} className="absolute left-3 bottom-2.5 text-slate-400" />
              <input className="input-field pl-9" placeholder="Título, nome do arquivo ou tag…" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer sm:pb-2.5">
              <input
                type="checkbox"
                className="checkbox-field"
                checked={expiring}
                onChange={(e) => {
                  setExpiring(e.target.checked);
                  setPage(1);
                }}
              />
              Só vencendo / vencidos
            </label>
            {hasFilters && (
              <button
                type="button"
                onClick={() => {
                  setSearchInput('');
                  setExpiring(false);
                  setPage(1);
                }}
                className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 sm:pb-2.5"
              >
                <X size={14} /> Limpar
              </button>
            )}
          </div>

          <p className="text-small">
            {selectedFolder ? (
              <>
                Pasta <strong>{selectedFolder.name}</strong>
              </>
            ) : (
              'Todas as pastas'
            )}
            {pagination && ` · ${pagination.total_items.toLocaleString('pt-BR')} ${pagination.total_items === 1 ? 'documento' : 'documentos'}`}
          </p>

          {/* Celular: cartões */}
          <div className="md:hidden space-y-2">
            {loading && items.length === 0 && (
              <p className="text-center text-slate-400 py-6">
                <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
              </p>
            )}
            {!loading && items.length === 0 && <p className="text-center text-slate-500 py-8">Nenhum documento aqui.</p>}
            {items.map((d) => (
              <div key={d.id} className={clsx('card p-3 space-y-2', loading && 'opacity-60')}>
                <div className="flex items-start gap-2">
                  <FileTypeIcon fileName={d.file_name} contentType={d.content_type} />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-800 [overflow-wrap:anywhere]">{d.title}</p>
                    <p className="text-small [overflow-wrap:anywhere]">
                      {d.file_name} · {formatBytes(d.size_bytes)}
                    </p>
                    {!selectedFolder && <p className="text-small">{d.folder_name}</p>}
                  </div>
                  <ExpiryBadge status={d.expiry_status} days={d.days_to_expire} />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 text-small">
                  <ExpiryLabel expiresAt={d.expires_at} days={d.days_to_expire} />
                  {actions(d)}
                </div>
              </div>
            ))}
            {pagination && <Pagination page={pagination.page} totalPages={pagination.total_pages} totalItems={pagination.total_items} onChange={setPage} />}
          </div>

          {/* Tabela */}
          <div className="table-container bg-white hidden md:block">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="table-header">
                  <tr>
                    <th>Documento</th>
                    {!selectedFolder && <th>Pasta</th>}
                    <th>Data</th>
                    <th>Validade</th>
                    <th className="w-40"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading && items.length === 0 && (
                    <tr>
                      <td colSpan={5} className="table-cell text-center text-slate-400">
                        <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
                      </td>
                    </tr>
                  )}
                  {!loading && items.length === 0 && (
                    <tr>
                      <td colSpan={5} className="table-cell text-center py-12 text-slate-500">
                        Nenhum documento aqui.{canWrite && ' Use "Enviar documentos" para começar.'}
                      </td>
                    </tr>
                  )}
                  {items.map((d) => (
                    <tr key={d.id} className={clsx('table-row', loading && 'opacity-60')}>
                      <td className="table-cell">
                        <div className="flex items-center gap-3">
                          <FileTypeIcon fileName={d.file_name} contentType={d.content_type} />
                          <div className="min-w-0">
                            <p className="font-medium text-slate-800">{d.title}</p>
                            <p className="text-small [overflow-wrap:anywhere]">
                              {d.file_name} · {formatBytes(d.size_bytes)}
                              {d.tags && <span className="text-slate-400"> · {d.tags}</span>}
                            </p>
                          </div>
                        </div>
                      </td>
                      {!selectedFolder && <td className="table-cell">{d.folder_name}</td>}
                      <td className="table-cell whitespace-nowrap">{d.doc_date ? formatDateBR(d.doc_date) : <span className="text-slate-400">—</span>}</td>
                      <td className="table-cell">
                        <div className="flex items-center gap-2">
                          <ExpiryLabel expiresAt={d.expires_at} days={d.days_to_expire} />
                          <ExpiryBadge status={d.expiry_status} days={d.days_to_expire} />
                        </div>
                      </td>
                      <td className="table-cell">{actions(d)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pagination && <Pagination page={pagination.page} totalPages={pagination.total_pages} totalItems={pagination.total_items} onChange={setPage} />}
          </div>
        </section>
      </div>

      {foldersOpen && (
        <Modal isOpen onClose={() => setFoldersOpen(false)} size="sm">
          <div className="px-5 py-4">{folderPanel}</div>
        </Modal>
      )}

      {uploading && (
        <DocumentUploadModal
          folders={folders}
          prefill={{ folder_id: folderId ?? undefined }}
          onClose={() => {
            setUploading(false);
            refreshAll();
          }}
          onUploaded={() => {
            setUploading(false);
            refreshAll();
          }}
          onError={setError}
        />
      )}

      {editing && (
        <DocumentEditModal
          document={editing}
          folders={folders}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refreshAll();
          }}
          onError={setError}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title="Excluir documento"
        message={`Excluir "${deleting?.title ?? ''}"? O arquivo deixa de aparecer e é apagado do armazenamento em 7 dias.`}
        confirmText="Excluir"
        severity="danger"
        loading={deleteBusy}
      />

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
